import { c as _c } from "react/compiler-runtime";
import constate from 'constate';
import { useState } from 'react';
import { NAVIGATION_HEADER_PORTAL_ID } from '@/idv2/components/constants';
import type { Options } from '../../types';
import type { NavigationHeaderBGVariant, NavigationHeaderPositionTypes } from '../navigation-header/types';
import { BOTTOM_ACTION_BOX_PORTAL_ID } from '../sticky-bottom-box/constants';
type Obj = Record<string, unknown>;
type LayoutOptionsArgs = {
  options?: Options;
  onClose?: () => void;
  // Scopes the nav-header portal so multiple Layouts can coexist on one page
  // (e.g. a Storybook gallery). Defaults to the shared global id in the real flow.
  navigationPortalId?: string;
  // Same, for the sticky bottom action box.
  bottomActionBoxPortalId?: string;
};
export type HeaderOptions = {
  background: NavigationHeaderBGVariant;
  position: NavigationHeaderPositionTypes;
};
export type FooterOptions = {
  visible: boolean;
  position: 'sticky' | 'relative';
  height: number;
};
export type BodyOptions = {
  fullBleed: boolean;
};
const hasUpdatedProp = ({
  newProps,
  oldProps
}: {
  newProps: Obj;
  oldProps: Obj;
}): boolean => {
  for (const key in newProps) {
    if (key in newProps && key in oldProps) {
      if (newProps[key] !== oldProps[key]) {
        return true;
      }
    }
  }
  return false;
};
const useLocalLayoutOptions = t0 => {
  const $ = _c(26);
  const {
    options,
    onClose,
    navigationPortalId: t1,
    bottomActionBoxPortalId: t2
  } = t0;
  const navigationPortalId = t1 === undefined ? NAVIGATION_HEADER_PORTAL_ID : t1;
  const bottomActionBoxPortalId = t2 === undefined ? BOTTOM_ACTION_BOX_PORTAL_ID : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "primary",
      position: "sticky"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [header, setHeader] = useState(t3);
  let t4;
  if ($[1] !== header) {
    t4 = headerOptions => {
      if (hasUpdatedProp({
        newProps: headerOptions,
        oldProps: header
      })) {
        setHeader(prev => ({
          ...prev,
          ...headerOptions
        }));
      }
    };
    $[1] = header;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const updateHeader = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      visible: true,
      position: "sticky",
      height: 0
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const [footer, setFooter] = useState(t5);
  let t6;
  if ($[4] !== footer) {
    t6 = footerOptions => {
      if (hasUpdatedProp({
        newProps: footerOptions,
        oldProps: footer
      })) {
        setFooter(prev_0 => ({
          ...prev_0,
          ...footerOptions
        }));
      }
    };
    $[4] = footer;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const updateFooter = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      fullBleed: false
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [body, setBody] = useState(t7);
  let t8;
  if ($[7] !== body) {
    t8 = bodyOptions => {
      if (hasUpdatedProp({
        newProps: bodyOptions,
        oldProps: body
      })) {
        setBody(prev_1 => ({
          ...prev_1,
          ...bodyOptions
        }));
      }
    };
    $[7] = body;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const updateBody = t8;
  let t9;
  if ($[9] !== header || $[10] !== updateHeader) {
    t9 = {
      options: header,
      set: updateHeader
    };
    $[9] = header;
    $[10] = updateHeader;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== footer || $[13] !== updateFooter) {
    t10 = {
      options: footer,
      set: updateFooter
    };
    $[12] = footer;
    $[13] = updateFooter;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== body || $[16] !== updateBody) {
    t11 = {
      options: body,
      set: updateBody
    };
    $[15] = body;
    $[16] = updateBody;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== bottomActionBoxPortalId || $[19] !== navigationPortalId || $[20] !== onClose || $[21] !== options || $[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = {
      options,
      onClose,
      navigationPortalId,
      bottomActionBoxPortalId,
      header: t9,
      footer: t10,
      body: t11
    };
    $[18] = bottomActionBoxPortalId;
    $[19] = navigationPortalId;
    $[20] = onClose;
    $[21] = options;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
const [LayoutOptionsProvider, useLayoutOptions] = constate(useLocalLayoutOptions);
export default LayoutOptionsProvider;
export { useLayoutOptions };